// The saloon's lived-in layer (James, 2026-10-23: "photos on the wall that are like little easter eggs,
// random objects that hint at things from the game"): years of somebody's bar on top of the baked room.
//
// - Framed sepia photos on the wall piers between the windows, printed from real scenes of the game
//   (the train, an island's waterfall, the town, the neighbours, a cow, the bosses). The prints and the
//   Company paperwork share one atlas: public/assets/saloon/posters/photos.jpg (_src/photos.py).
// - A table each in the four booths that somebody just got up from: an abandoned poker game, a chess
//   game down to the black king (the Black Court), a candle in a bottle with a letter, the paper.
// - Boss tells about the place: a piranha in a fishbowl on the bar (the Angler), three dog bowls at the
//   fore end of the bar (Cerberus), a pot-belly stove in the corner (the Bulwark's stove soldiers), a
//   toaster mounted over the aft door like a trophy head. A recalled safety harness on the coat tree.
// - The bar worked: a rag, a bell, a bowl of bolts, spittoons at the foot rail; a runner down the aisle.
//
// Every one of them is modelled in Blender (house rule 14b, James 2026-10-24: never hacked from code
// primitives): public/assets/saloon/_src/dressing.py builds, places and exports them all to dressing.glb,
// already in the car's frame, with their paint packed in one atlas. That leaves four materials, so four
// draw calls for the lot: opaque (atlas + roughness/metal map), glass, glow (the stove's mica, the
// candle) and print (the photo atlas, which loads once here rather than riding in the GLB).
//
// Never the hotdogs (house rule 16). Nothing here adds a light.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const ASSET = BASE + 'assets/saloon/';
/** Room light on unlit paper: what the bake would have given it (props.ts ROOM). */
const ROOM = new THREE.Color(0.95, 0.82, 0.66);

/** Atlas cells, px in a 2048² sheet: [x, y, w, h] from the top left (_src/photos.py prints these; dressing.py
 *  keeps a copy to map the prints in Blender). */
export const PRINTS: Record<string, [number, number, number, number]> = {
  island_edge: [0, 0, 480, 372], town: [512, 0, 480, 372], robots: [1024, 0, 480, 372], herd: [1536, 0, 480, 372],
  cerberus: [0, 512, 480, 372], angler: [512, 512, 480, 372], posse: [1024, 512, 480, 372], island_wide: [1536, 512, 480, 372],
  train: [0, 1024, 480, 372], employee: [512, 1024, 360, 480], trainee: [1024, 1024, 480, 300], gazette: [1536, 1024, 400, 500],
  water_sign: [0, 1536, 1000, 250], letter: [1024, 1536, 340, 440], cards: [1536, 1536, 200, 280], deck: [1792, 1536, 200, 280],
  sergeant: [0, 1796, 240, 240], carrier: [256, 1796, 240, 240], form: [512, 1796, 240, 240],
};

export class SaloonDressing {
  readonly group = new THREE.Group();
  readonly ready: Promise<void>;

  constructor() {
    this.group.name = 'saloon-dressing';
    this.ready = this.load().catch((e) => console.warn('saloon dressing failed to load', e));
  }

  private async load() {
    const [gltf, photos] = await Promise.all([
      new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(ASSET + 'dressing.glb'),
      new THREE.TextureLoader().loadAsync(ASSET + 'posters/photos.jpg'),
    ]);
    photos.colorSpace = THREE.SRGBColorSpace;
    photos.flipY = false; // glTF UVs
    photos.anisotropy = 4;
    gltf.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const src = m.material as THREE.MeshStandardMaterial;
      const map = src.map;
      if (map) map.anisotropy = 4;
      switch (src.name) {
        case 'dress_glass':
          m.material = new THREE.MeshStandardMaterial({ map, roughness: 0.03, metalness: 0.1, transparent: true, opacity: 0.13, depthWrite: false, envMapIntensity: 1.4 }); // clear, with the lamps glinting off the cut facets
          m.renderOrder = 2;
          break;
        case 'dress_glow':
          m.material = new THREE.MeshBasicMaterial({ map: (src.emissiveMap ?? map) });
          break;
        case 'dress_print':
          m.material = new THREE.MeshBasicMaterial({ map: photos, color: ROOM });
          break;
        default: // dress_opaque: the atlas and its roughness/metal map, as exported
          src.envMapIntensity = 0.6;
      }
      if (m.material !== src) src.dispose();
      m.matrixAutoUpdate = false;
      m.updateMatrix();
    });
    this.group.add(gltf.scene);
  }
}
